import type { ReactNode } from 'react';
import { selectRevealView, useGame, type GameState } from '@/store/game';
import { serverNow } from '@/lib/api';
import { useDesktop } from '@/lib/useDesktop';
import { WideContext } from '@/formats/split';

/**
 * Where the battle stands, for the looks that colour the page by phase (data-phase on the frame; the bold look reads
 * it). The reveal splits in two: the answer view and the leaderboard. Pages without a battle get none.
 */
export type ScreenPhase = 'lobby' | 'question' | 'reveal' | 'leaderboard' | 'podium' | 'ended';
export function phaseOf(s: Pick<GameState, 'snapshot' | 'revealSeen' | 'joined'>, now: number = serverNow()): ScreenPhase | undefined {
  const snap = s.snapshot;
  if (!snap) return undefined;
  switch (snap.status) {
    case 'lobby': return 'lobby';
    case 'question': return 'question';
    case 'reveal': return selectRevealView(s, now) === 'board' ? 'leaderboard' : 'reveal';
    case 'podium': return 'podium';
    default: return 'ended';
  }
}

/** The frame's width: a phone column everywhere but a desktop, where a question gets 1200 px (two columns) and the rest 720 px. */
export function frameWidth(desktop: boolean, phase: ScreenPhase | undefined): string {
  if (!desktop) return 'max-w-md px-4';
  return phase === 'question' ? 'max-w-[1200px] px-8' : 'max-w-[720px] px-8';
}

/**
 * The player page frame: one phone-width column, 16 px gutters, clear of the notch and the home bar. On a desktop
 * (lib/useDesktop, 26 Oct 2026) the frame widens, carries `data-desktop`, and a question's formats split into two columns.
 * `header` (the in-game AppHeader) sits outside <main>, as the page's banner; the screen's content is the main landmark.
 * The frame carries `data-phase` (lobby, question, reveal, leaderboard, podium, ended) from the game store while a
 * battle is on, so a look can colour the whole page by phase. The offline line (components/OfflineBanner) is not here:
 * the game page and the join page render it once, above this frame, so a screen leaving through a transition never
 * shows a second one.
 */
export function Screen({ children, header, className = '', center = false, label, phase: given }: {
  children: ReactNode; header?: ReactNode; className?: string; center?: boolean; label?: string;
  /** A screen that knows its own phase says so (the podium shows under the ended status too); otherwise the store decides. */
  phase?: ScreenPhase;
}) {
  const fromStore = useGame(phaseOf);
  const phase = given ?? fromStore;
  const desktop = useDesktop();
  return (
    <div data-phase={phase} data-desktop={desktop ? '' : undefined} className={`mx-auto flex min-h-dvh w-full ${frameWidth(desktop, phase)} flex-col gap-5 pb-[max(2rem,env(safe-area-inset-bottom))] pt-[max(0.75rem,env(safe-area-inset-top))]`}>
      {header}
      <main aria-label={label} className={`flex flex-1 flex-col gap-5 ${center ? 'justify-center' : ''} ${className}`}>
        <WideContext.Provider value={desktop && phase === 'question'}>{children}</WideContext.Provider>
      </main>
    </div>
  );
}
